import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { 
  Globe, 
  Plus, 
  Settings, 
  ExternalLink, 
  Trash2, 
  Edit3,
  Eye,
  Download,
  Share2,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Play,
  Pause,
  RefreshCw,
  Sparkles,
  Zap,
  Star,
  Heart
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const WebsiteBuilder = () => {
  const { user } = useAuth();
  const [mainDomain, setMainDomain] = useState('');
  const [isBuilding, setIsBuilding] = useState(false);
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock data for demonstration
  useEffect(() => {
    // Simulate loading websites
    setTimeout(() => {
      setWebsites([
        {
          id: 1,
          domain: 'mywebsite.com',
          status: 'active',
          buildDate: '2024-01-15',
          lastModified: '2024-01-20',
          visitors: 1250,
          pages: 8,
          theme: 'Modern Blue'
        },
        {
          id: 2,
          domain: 'shop.example.com',
          status: 'building',
          buildDate: '2024-01-18',
          lastModified: '2024-01-18',
          visitors: 0,
          pages: 0,
          theme: 'Classic White'
        },
        {
          id: 3,
          domain: 'store.demo.net',
          status: 'paused',
          buildDate: '2024-01-10',
          lastModified: '2024-01-15',
          visitors: 890,
          pages: 12,
          theme: 'Dark Pro'
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const handleBuildWebsite = async () => {
    if (!mainDomain.trim()) {
      toast.error('Vui lòng nhập tên miền chính');
      return;
    }

    // Validate domain format
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(mainDomain)) {
      toast.error('Tên miền không hợp lệ. Vui lòng nhập đúng định dạng (ví dụ: example.com)');
      return;
    }

    setIsBuilding(true);
    
    // Simulate building process
    setTimeout(() => {
      const newWebsite = {
        id: Date.now(),
        domain: mainDomain,
        status: 'building',
        buildDate: new Date().toISOString().split('T')[0],
        lastModified: new Date().toISOString().split('T')[0],
        visitors: 0,
        pages: 0,
        theme: 'Default Theme'
      };

      setWebsites(prev => [newWebsite, ...prev]);
      setMainDomain('');
      setIsBuilding(false);
      toast.success('Đã bắt đầu xây dựng website!');
    }, 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'building':
        return 'bg-blue-100 text-blue-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'building':
        return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'paused':
        return <Pause className="w-4 h-4" />;
      case 'error':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Hoạt động';
      case 'building':
        return 'Đang xây dựng';
      case 'paused':
        return 'Tạm dừng';
      case 'error':
        return 'Lỗi';
      default:
        return 'Không xác định';
    }
  };

  const handleDeleteWebsite = (id) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa website này?')) {
      setWebsites(prev => prev.filter(website => website.id !== id));
      toast.success('Đã xóa website');
    }
  };

  const handlePauseResume = (id) => {
    setWebsites(prev => prev.map(website => 
      website.id === id 
        ? { ...website, status: website.status === 'paused' ? 'active' : 'paused' }
        : website
    ));
    toast.success('Đã cập nhật trạng thái website');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  return (
    <div className="space-y-6 p-6  from-blue-50 via-white to-purple-50 min-h-screen">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0"
      >
        <div>
          <motion.h1 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
          >
            Website Builder
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-gray-600 mt-1 flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4 text-yellow-500" />
            Xây dựng website cho cửa hàng của bạn
          </motion.p>
        </div>
        <motion.div 
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="flex items-center space-x-2"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <Globe className="w-6 h-6 text-blue-600" />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Build Website Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-white/50 relative overflow-hidden"
      >
        {/* Animated background elements */}
        <motion.div
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full opacity-20"
        />
        <motion.div
          animate={{ 
            rotate: [360, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -bottom-8 -left-8 w-16 h-16 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full opacity-20"
        />
        
        <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-4 lg:space-y-0 lg:space-x-4 relative z-10">
          <div className="flex-1">
            <motion.label 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
            >
              <Zap className="w-4 h-4 text-yellow-500" />
              Tên miền chính
            </motion.label>
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="relative"
            >
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={mainDomain}
                onChange={(e) => setMainDomain(e.target.value)}
                placeholder="Nhập tên miền (ví dụ: mywebsite.com)"
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/50 backdrop-blur-sm"
                disabled={isBuilding}
              />
            </motion.div>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xs text-gray-500 mt-1 flex items-center gap-1"
            >
              <Star className="w-3 h-3 text-blue-500" />
              Tên miền sẽ được sử dụng làm địa chỉ chính cho website của bạn
            </motion.p>
          </div>
          
          <motion.button
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            }}
            whileTap={{ scale: 0.95 }}
            onClick={handleBuildWebsite}
            disabled={isBuilding || !mainDomain.trim()}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hover:from-blue-600 hover:via-purple-600 hover:to-pink-600 text-white rounded-lg transition-all duration-300 flex items-center space-x-2 font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: "-100%" }}
              whileHover={{ x: "100%" }}
              transition={{ duration: 0.6 }}
            />
            {isBuilding ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Đang xây dựng...</span>
              </>
            ) : (
              <>
                <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                <span>Build Website</span>
              </>
            )}
          </motion.button>
        </div>
      </motion.div>

      {/* Websites List */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/50"
      >
        <div className="p-6 border-b border-gray-200/50">
          <motion.h2 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="text-xl font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent"
          >
            Danh sách Website
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="text-gray-600 mt-1 flex items-center gap-2"
          >
            <Heart className="w-4 h-4 text-red-500" />
            Quản lý các website đã xây dựng
          </motion.p>
        </div>

        {loading ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-8 text-center"
          >
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"
            />
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-gray-600"
            >
              Đang tải danh sách website...
            </motion.p>
          </motion.div>
        ) : websites.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7 }}
            className="p-8 text-center"
          >
            <motion.div
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Globe className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            </motion.div>
            <motion.h3 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="text-lg font-medium text-gray-900 mb-2"
            >
              Chưa có website nào
            </motion.h3>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="text-gray-600"
            >
              Bắt đầu xây dựng website đầu tiên của bạn
            </motion.p>
          </motion.div>
                 ) : (
           <div className="divide-y divide-gray-200/50">
             {websites.map((website, index) => (
               <motion.div
                 key={website.id}
                 initial={{ opacity: 0, y: 20, scale: 0.95 }}
                 animate={{ opacity: 1, y: 0, scale: 1 }}
                 exit={{ opacity: 0, y: -20, scale: 0.95 }}
                 transition={{ 
                   delay: index * 0.1,
                   duration: 0.5,
                   type: "spring",
                   stiffness: 100
                 }}
                 whileHover={{ 
                   scale: 1.02,
                   backgroundColor: "rgba(249, 250, 251, 0.8)"
                 }}
                 className="p-6 transition-all duration-300 relative overflow-hidden"
               >
                 {/* Hover effect background */}
                 <motion.div
                   className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-purple-50/50"
                   initial={{ opacity: 0 }}
                   whileHover={{ opacity: 1 }}
                   transition={{ duration: 0.3 }}
                 />
                 <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0 relative z-10">
                   <div className="flex-1">
                     <div className="flex items-center space-x-3 mb-2">
                       <motion.div
                         animate={{ rotate: [0, 360] }}
                         transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                       >
                         <Globe className="w-5 h-5 text-blue-600" />
                       </motion.div>
                       <h3 className="text-lg font-semibold text-gray-900">{website.domain}</h3>
                       <motion.span 
                         initial={{ scale: 0 }}
                         animate={{ scale: 1 }}
                         transition={{ delay: 0.2, type: "spring" }}
                         className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(website.status)}`}
                       >
                         {getStatusIcon(website.status)}
                         <span>{getStatusText(website.status)}</span>
                       </motion.span>
                     </div>
                     
                     <motion.div 
                       initial={{ opacity: 0, y: 10 }}
                       animate={{ opacity: 1, y: 0 }}
                       transition={{ delay: 0.3 }}
                       className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600"
                     >
                       <motion.div
                         whileHover={{ scale: 1.05 }}
                         className="p-2 rounded-lg bg-white/50 backdrop-blur-sm"
                       >
                         <span className="font-medium">Ngày tạo:</span> {formatDate(website.buildDate)}
                       </motion.div>
                       <motion.div
                         whileHover={{ scale: 1.05 }}
                         className="p-2 rounded-lg bg-white/50 backdrop-blur-sm"
                       >
                         <span className="font-medium">Cập nhật:</span> {formatDate(website.lastModified)}
                       </motion.div>
                       <motion.div
                         whileHover={{ scale: 1.05 }}
                         className="p-2 rounded-lg bg-white/50 backdrop-blur-sm"
                       >
                         <span className="font-medium">Lượt truy cập:</span> {website.visitors.toLocaleString()}
                       </motion.div>
                       <motion.div
                         whileHover={{ scale: 1.05 }}
                         className="p-2 rounded-lg bg-white/50 backdrop-blur-sm"
                       >
                         <span className="font-medium">Số trang:</span> {website.pages}
                       </motion.div>
                     </motion.div>
                   </div>
                   
                   <div className="flex items-center space-x-2">
                     <motion.button
                       whileHover={{ 
                         scale: 1.1,
                         rotate: 5,
                         boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
                       }}
                       whileTap={{ scale: 0.9 }}
                       onClick={() => handlePauseResume(website.id)}
                       className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-300"
                       title={website.status === 'paused' ? 'Tiếp tục' : 'Tạm dừng'}
                     >
                       {website.status === 'paused' ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                     </motion.button>
                     
                     <motion.button
                       whileHover={{ 
                         scale: 1.1,
                         rotate: 5,
                         boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.3)"
                       }}
                       whileTap={{ scale: 0.9 }}
                       className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-300"
                       title="Xem website"
                     >
                       <Eye className="w-4 h-4" />
                     </motion.button>
                     
                     <motion.button
                       whileHover={{ 
                         scale: 1.1,
                         rotate: 5,
                         boxShadow: "0 10px 15px -3px rgba(34, 197, 94, 0.3)"
                       }}
                       whileTap={{ scale: 0.9 }}
                       className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-300"
                       title="Chỉnh sửa"
                     >
                       <Edit3 className="w-4 h-4" />
                     </motion.button>
                     
                     <motion.button
                       whileHover={{ 
                         scale: 1.1,
                         rotate: 5,
                         boxShadow: "0 10px 15px -3px rgba(168, 85, 247, 0.3)"
                       }}
                       whileTap={{ scale: 0.9 }}
                       className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-300"
                       title="Cài đặt"
                     >
                       <Settings className="w-4 h-4" />
                     </motion.button>
                     
                     <motion.button
                       whileHover={{ 
                         scale: 1.1,
                         rotate: 5,
                         boxShadow: "0 10px 15px -3px rgba(239, 68, 68, 0.3)"
                       }}
                       whileTap={{ scale: 0.9 }}
                       onClick={() => handleDeleteWebsite(website.id)}
                       className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300"
                       title="Xóa website"
                     >
                       <Trash2 className="w-4 h-4" />
                     </motion.button>
                   </div>
                 </div>
               </motion.div>
             ))}
           </div>
         )}
       </motion.div>
     </div>
   );
 };

export default WebsiteBuilder; 